/**
 * Files the operator attaches to a chat message (MA-451). The message stores a
 * record per file; the file itself lives with the conversation (desktop:
 * `<conversation>/attachments/`), so it is archived, trashed and restored with
 * it. What a model is sent is built from the record at send time
 * ({@link toModelMessages}) — the stored text stays exactly what the operator
 * wrote.
 *
 * An image goes to the model as an image only on the turn it is attached;
 * later turns name it and say where it is (operator decision 2026-10-13,
 * ADR-0002's exception for attachments). Runtime-agnostic: no Node imports.
 */
import type { ContentBlock, ConversationMessage, ImageBlock } from './agent/types.js';
import type { ImageMimeType } from './mime.js';

/** One attached file, as the message stores it. Written by the server only. */
export interface StoredAttachment {
  /** Upload id — names the file in every route. */
  id: string;
  /** The file's name on the operator's device — shown, never used as a path. */
  name: string;
  /** Read from the file's bytes for images and PDFs; otherwise the browser's label. */
  mimeType: string;
  size: number;
  /** File name in the conversation's attachments folder (`<id><ext>`). */
  file: string;
}

/** Largest file the composer accepts. */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/** Most files one message carries. */
export const MAX_ATTACHMENTS_PER_MESSAGE = 5;

/** A message as the store hands it to a turn: the API shape plus its attachments. */
export interface HistoryMessage {
  role: 'user' | 'assistant';
  content: string | ContentBlock[];
  attachments?: StoredAttachment[] | undefined;
}

/** How a turn's messages show their attachments to the model. */
export interface AttachmentView {
  /** Where the agent can open an attachment (the file's current path). */
  pathOf: (attachment: StoredAttachment) => string;
  /** read_file on this transport returns an image the model can see (the CLI's MCP path). */
  readFileShowsImages: boolean;
  /**
   * Images to send as images, by attachment id (the base64 bytes): a turn
   * passes only its own message's, and none when the model can't take them.
   * Every other image is named in the note.
   */
  inlineImages: ReadonlyMap<string, { mimeType: ImageMimeType; data: string }>;
}

function formatSize(bytes: number): string {
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

/**
 * The note naming a message's attachments — from OneStone, so the model never
 * reads it as something the operator wrote. `shown` holds the ids sent as
 * images with this message.
 */
export function attachmentNote(
  attachments: readonly StoredAttachment[],
  view: Pick<AttachmentView, 'pathOf' | 'readFileShowsImages'>,
  shown: ReadonlySet<string>,
): string {
  const lines = attachments.map((a) => {
    const where = view.pathOf(a);
    return `- ${a.name} (${a.mimeType}, ${formatSize(a.size)}): ${where}${shown.has(a.id) ? ' — shown above' : ''}`;
  });
  const unseenImage = attachments.some((a) => a.mimeType.startsWith('image/') && !shown.has(a.id));
  const howToLook = !unseenImage
    ? 'Open a file with read_file to read it.'
    : view.readFileShowsImages
      ? 'Open a file with read_file to read it; read_file shows images.'
      : "Open a file with read_file to read it. Here read_file can't show an image, so an image not shown above can't be viewed — ask the operator to attach it again if you need to see it.";
  return [
    '<attachments>',
    'From OneStone, not the operator: the operator attached these files to this message (kept with the conversation).',
    ...lines,
    howToLook,
    '</attachments>',
  ].join('\n');
}

/**
 * What a model is sent: each message with attachments carries the note after
 * its text, and an attachment in `view.inlineImages` goes as an image block
 * before the text (images first, as Anthropic recommends). Messages without
 * attachments come back as they were.
 */
export function toModelMessages(messages: readonly HistoryMessage[], view: AttachmentView): ConversationMessage[] {
  return messages.map((m) => {
    if (!m.attachments || m.attachments.length === 0) return { role: m.role, content: m.content };
    const images: ImageBlock[] = [];
    const shown = new Set<string>();
    for (const a of m.attachments) {
      const image = view.inlineImages.get(a.id);
      if (!image) continue;
      images.push({ type: 'image', source: { type: 'base64', media_type: image.mimeType, data: image.data } });
      shown.add(a.id);
    }
    const text: ContentBlock[] =
      typeof m.content === 'string' ? (m.content ? [{ type: 'text', text: m.content }] : []) : m.content;
    const note: ContentBlock = { type: 'text', text: attachmentNote(m.attachments, view, shown) };
    return { role: m.role, content: [...images, ...text, note] };
  });
}
